import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, CheckCircle, Phone, CalendarDays } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Checkbox } from '../components/ui/checkbox';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { SERVICES } from '../lib/services-data';
import { COMPANY_INFO } from '../lib/utils';
import axios from 'axios';
import { SEOHead } from '../components/SEOHead';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";

const fadeUp = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.5 } }
};

const CRENEAUX = [
  'Matin (8h-12h)',
  'Après-midi (14h-17h)',
  'Soir (17h-19h)',
];

export default function RendezVousPage() {
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [form, setForm] = useState({
    nom: '', telephone: '', email: '', service: '',
    date_preferee: '', creneau: '', message: '',
    agency: 'artois.ptitservices', consent_rgpd: false, honeypot: ''
  });
  const [errors, setErrors] = useState({});

  const validate = () => {
    const e = {};
    if (!form.nom.trim()) e.nom = 'Le nom est obligatoire';
    if (!form.telephone.trim()) e.telephone = 'Le téléphone est obligatoire';
    if (!form.email.trim() || !/\S+@\S+\.\S+/.test(form.email)) e.email = 'Email invalide';
    if (!form.service) e.service = 'Sélectionnez un service';
    if (!form.date_preferee) e.date_preferee = 'Choisissez une date';
    if (!form.creneau) e.creneau = 'Choisissez un créneau';
    if (!form.consent_rgpd) e.consent_rgpd = 'Vous devez accepter la politique de confidentialité';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setLoading(true);
    try {
      const response = await axios.post(`${BACKEND_URL}/api/appointments`, form);
      if (response.data.success) {
        setSuccess(true);
        toast.success('Rendez-vous demandé !', { description: response.data.message });
      }
    } catch (err) {
      toast.error('Erreur', { description: 'Une erreur est survenue. Veuillez réessayer.' });
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="py-20">
        <div className="max-w-lg mx-auto px-4 text-center">
          <div className="w-16 h-16 rounded-full bg-[hsl(var(--brand-mint))] flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="h-8 w-8 text-[hsl(var(--primary))]" />
          </div>
          <h1 className="font-['Space_Grotesk'] text-2xl font-semibold mb-3">Demande de rendez-vous envoyée !</h1>
          <p className="text-muted-foreground mb-6">Nous vous confirmerons le créneau par téléphone dans les plus brefs délais.</p>
          <Link to="/"><Button>Retour à l'accueil</Button></Link>
        </div>
      </div>
    );
  }

  return (
    <div>
      <SEOHead
        title="Prendre rendez-vous — évaluation gratuite"
        description="Prenez rendez-vous gratuitement pour une évaluation de vos besoins à domicile. Services à la personne : ménage, jardinage, repassage. Crédit d'impôt 50%."
        path="/rendez-vous"
      />
      {/* Hero */}
      <section className="bg-[linear-gradient(135deg,hsla(195,20%,97%,1)_0%,hsla(182,40%,94%,1)_55%,hsla(195,20%,97%,1)_100%)] py-12 sm:py-16">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="flex items-center gap-2 text-sm text-muted-foreground mb-6">
            <Link to="/" className="hover:text-foreground">Accueil</Link>
            <span>/</span>
            <span className="text-foreground font-medium">Prendre rendez-vous</span>
          </div>
          <Badge className="mb-4 px-5 py-2 text-base font-semibold rounded-full bg-[hsl(var(--primary))] text-white hover:bg-[hsl(var(--primary))]/90">Rendez-vous</Badge>
          <h1 className="font-['Space_Grotesk'] text-3xl sm:text-4xl font-semibold tracking-tight mb-3">Prendre rendez-vous</h1>
          <p className="text-muted-foreground max-w-xl">Planifiez un rendez-vous gratuit et sans engagement. Nous viendrons évaluer vos besoins à votre domicile.</p>
        </div>
      </section>

      {/* Form */}
      <section className="py-12 sm:py-16">
        <div className="max-w-3xl mx-auto px-4 sm:px-6">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            <div className="lg:col-span-2">
              <motion.form onSubmit={handleSubmit} initial="hidden" animate="visible" variants={fadeUp} data-testid="appointment-form">
                <Card className="p-6 sm:p-8 space-y-5">
                  <input type="text" name="hp" value={form.honeypot} onChange={e => setForm(p => ({...p, honeypot: e.target.value}))} className="hidden" tabIndex={-1} autoComplete="off" />

                  <div>
                    <Label>Agence *</Label>
                    <Select value={form.agency} onValueChange={v => setForm(p => ({...p, agency: v}))}>
                      <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="artois.ptitservices">Agence Artois</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="nom">Nom complet *</Label>
                      <Input id="nom" value={form.nom} onChange={e => setForm(p => ({...p, nom: e.target.value}))} placeholder="Votre nom" className="mt-1" />
                      {errors.nom && <p className="text-xs text-destructive mt-1">{errors.nom}</p>}
                    </div>
                    <div>
                      <Label htmlFor="telephone">Téléphone *</Label>
                      <Input id="telephone" value={form.telephone} onChange={e => setForm(p => ({...p, telephone: e.target.value}))} placeholder="06 00 00 00 00" className="mt-1" />
                      {errors.telephone && <p className="text-xs text-destructive mt-1">{errors.telephone}</p>}
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="email">Email *</Label>
                    <Input id="email" type="email" value={form.email} onChange={e => setForm(p => ({...p, email: e.target.value}))} placeholder="votre@email.com" className="mt-1" />
                    {errors.email && <p className="text-xs text-destructive mt-1">{errors.email}</p>}
                  </div>

                  <div>
                    <Label>Service concerné *</Label>
                    <Select value={form.service} onValueChange={v => setForm(p => ({...p, service: v}))}>
                      <SelectTrigger className="mt-1"><SelectValue placeholder="Sélectionnez un service" /></SelectTrigger>
                      <SelectContent>
                        {SERVICES.map(s => (
                          <SelectItem key={s.id} value={s.id}>{s.title}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {errors.service && <p className="text-xs text-destructive mt-1">{errors.service}</p>}
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="date">Date préférée *</Label>
                      <Input id="date" type="date" value={form.date_preferee} onChange={e => setForm(p => ({...p, date_preferee: e.target.value}))} className="mt-1" min={new Date().toISOString().split('T')[0]} />
                      {errors.date_preferee && <p className="text-xs text-destructive mt-1">{errors.date_preferee}</p>}
                    </div>
                    <div>
                      <Label>Créneau horaire *</Label>
                      <Select value={form.creneau} onValueChange={v => setForm(p => ({...p, creneau: v}))}>
                        <SelectTrigger className="mt-1"><SelectValue placeholder="Sélectionnez" /></SelectTrigger>
                        <SelectContent>
                          {CRENEAUX.map(c => (
                            <SelectItem key={c} value={c}>{c}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      {errors.creneau && <p className="text-xs text-destructive mt-1">{errors.creneau}</p>}
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="message">Message (optionnel)</Label>
                    <Textarea id="message" value={form.message} onChange={e => setForm(p => ({...p, message: e.target.value}))} placeholder="Précisez vos besoins..." className="mt-1" rows={3} />
                  </div>

                  <div>
                    <label className="flex items-start gap-2 cursor-pointer">
                      <Checkbox checked={form.consent_rgpd} onCheckedChange={v => setForm(p => ({...p, consent_rgpd: v}))} className="mt-0.5" />
                      <span className="text-xs text-muted-foreground">J'accepte que mes données soient utilisées pour traiter ma demande. *</span>
                    </label>
                    {errors.consent_rgpd && <p className="text-xs text-destructive mt-1">{errors.consent_rgpd}</p>}
                  </div>

                  <Button type="submit" disabled={loading} className="w-full bg-[hsl(var(--accent))] hover:bg-[hsl(var(--accent))]/90 text-white" data-testid="appointment-form-submit-button">
                    {loading ? 'Envoi en cours...' : 'Demander un rendez-vous'}
                    <CalendarDays className="ml-2 h-4 w-4" />
                  </Button>
                </Card>
              </motion.form>
            </div>

            <div className="space-y-4">
              <Card className="p-5 bg-[hsl(var(--brand-mint))]/20 border-0">
                <h3 className="font-['Space_Grotesk'] font-semibold text-sm mb-3">Rendez-vous gratuit</h3>
                <p className="text-xs text-muted-foreground">Notre équipe se déplace gratuitement à votre domicile pour évaluer vos besoins.</p>
              </Card>
              <Card className="p-5">
                <h3 className="font-['Space_Grotesk'] font-semibold text-sm mb-3">Appelez-nous</h3>
                <a href={`tel:${COMPANY_INFO.phoneRaw}`} className="flex items-center gap-2 text-sm text-[hsl(var(--primary))] font-medium">
                  <Phone className="h-4 w-4" />
                  {COMPANY_INFO.phone}
                </a>
              </Card>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
